import { useState, useRef, useEffect, Fragment } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { isNameUnique } from '../utils/slug';
import { apiGetPromoToken, apiRegeneratePromoToken, apiSetPromoPassword } from '../api';
import { apiCreateInvitation, apiGetBandMembers, apiSetBandMember, apiUpdateBand, apiGeocodeBandAddress } from '../api';
import type { BandRole, BandMember } from '../api';
import { showConfirm } from '../components/ConfirmDialog';
import AirportInput from '../components/AirportInput';
import BandFilesSection from '../components/BandFilesSection';
import PromoContentSettings from '../components/PromoContentSettings';
import BandBackupSection from '../components/BandBackupSection';
import BandImportSection from '../components/BandImportSection';
import CountryInput from '../components/CountryInput';
import PasswordInput from '../components/PasswordInput';
import AccessPanel from '../components/AccessPanel';
import { derivedRoleLabelKey, memberPermissions, type Permission } from '../lib/permissions';
import { useWriteGate } from '../lib/writeGate';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

// Tabs for the settings page (structure-only refactor, spec docs/SPEC-BAND-SETTINGS-TABS.md).
type SettingsTab = 'identity' | 'files' | 'members' | 'advanced';
const VALID_SETTINGS_TABS = new Set<SettingsTab>(['identity', 'files', 'members', 'advanced']);

// Role label keys for the member list (shared with UsersPage).
const ROLE_LABEL: Record<BandRole, string> = {
  owner: 'users.roleOwner',
  editor: 'users.roleEditor',
  tech: 'users.roleTech',
  // `crew` = the PRE-RENAME spelling of the same role. The DB still accepts it
  // (safe rollback), so a payload can carry it: it must render as Technician, not
  // as a raw i18n key.
  crew: 'users.roleTech',
  viewer: 'users.roleViewer',
};

export default function BandSettingsPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeProfileId, getProfile, updateProfile, deleteProfile, profiles } = useProfileContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const profile = activeProfileId ? getProfile(activeProfileId) : null;
  // ── Write gate (A2, 2026-09-25) — AUTHORITY writes, plain `canWrite` ───────
  // Everything this page persists is mounted AFTER the server's global read-only
  // guard (`/api/bands` + the member/invite/promo routes, server/index.js:572
  // vs the guard at :519) and carries NO delegable capability → the exact gate
  // is the plain `canWrite` (account ceiling), never a `canWriteCap`.
  // NB: these surfaces are gated at their CALLER on purpose — `useAuth()`
  // THROWS inside `useProfileContext` (`App.tsx` nests <AuthProvider> INSIDE
  // <ProfileProvider>), so the hook's own create/update/deleteProfile stay
  // ungated and every page freezes its own controls.
  const writeGate = useWriteGate(activeProfileId);
  const canWrite = writeGate.canWrite;
  const { user } = useAuth();
  const isSuperAdmin = user?.global_role === 'superadmin';
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  // ── DELETE needs OWNER, not editor (2026-09-25, lot A2-bis) ───────────────
  // `router.delete('/:id', requireBandAccess('owner'))` — server/routes/bands.js:143
  // — while the PUT next door only needs editor+ (:114). `canWrite` above is the
  // plain band-editor predicate (owner OR editor), so gating the danger zone on
  // it ALONE offered 'Delete band' to an editor → the click always ended in a
  // 403: the exact bug class ("the UI offers an action the server refuses")
  // eliminated across the app today. A superadmin owns every band implicitly;
  // the global read-only ceiling and the showcase freeze still cut the delete,
  // which is why this is `canWrite AND (superadmin OR band owner)`, never a
  // bare role test.
  const canDeleteBand = canWrite && (isSuperAdmin || profile?.my_role === 'owner');
  const [name, setName] = useState(profile?.name || '');
  const [color, setColor] = useState(profile?.color || '#e94560');
  const [country, setCountry] = useState(profile?.country || '');
  // ── Home base (2026-10-11) — the van's departure point for the automatic
  // « band -> venue » distance leg. The address is geocoded server-side (one
  // click), then the coordinates are stored on the band.
  const [address, setAddress] = useState(profile?.address || '');
  const [defaultAirport, setDefaultAirport] = useState(profile?.default_airport || '');
  // Two DISTINCT departure points (2026-11-02, LOT U2c): the old single field
  // with an air/rail toggle (which WIPED the value on click) is gone. Each
  // column owns its own catalogue — IATA airport vs UIC station — and neither
  // ever clears the other.
  const [defaultStation, setDefaultStation] = useState(profile?.default_station || '');
  const [postalCode, setPostalCode] = useState(profile?.postal_code || '');
  const [city, setCity] = useState(profile?.city || '');
  const [locating, setLocating] = useState(false);
  const [locateMsg, setLocateMsg] = useState<string | null>(null);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  // Promo kit public link (editor+ can see/generate it; a viewer's fetch 403s
  // and the link is simply hidden).
  const [promoToken, setPromoToken] = useState<string | null>(null);
  const [promoLinkCopied, setPromoLinkCopied] = useState(false);
  // Password of the public Promo kit link (2026-10-16). The editor READS IT
  // BACK in clear (reversible encryption server-side) — the field is simply
  // frozen and empty for an account that may not write, since the server only
  // decrypts it for a caller holding the write right.
  const [promoPassword, setPromoPassword] = useState('');
  // Last value known to be STORED (what the field compared against to enable Save).
  const [promoSavedPassword, setPromoSavedPassword] = useState('');
  const [promoHasPassword, setPromoHasPassword] = useState(false);
  const [promoPasswordSaving, setPromoPasswordSaving] = useState(false);
  const [promoPasswordMsg, setPromoPasswordMsg] = useState<string | null>(null);
  const [promoPasswordError, setPromoPasswordError] = useState<string | null>(null);
  // Invite a member into this band (owner / superadmin only — backend canManageBand).
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<BandRole>('viewer');
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  // Whether the current user may invite people into this band: an owner of the
  // band OR a superadmin (bandRole maps superadmin → 'owner'). Viewers and
  // editors do not see the invite section.
  const canInvite = profile?.my_role === 'owner';

  // ---- Members & their capability matrix (owner/superadmin only) ----
  // The owner grants the fine-grained capabilities here (chantier C,
  // 2026-09-25) — one screen per member, replacing the two ad-hoc checkboxes
  // (`finance_access` / `layout_access`). Backend PUT
  // /bands/:id/members/:userId enforces owner/superadmin and refuses any
  // unknown or non-delegable capability name with a 400.
  const [members, setMembers] = useState<BandMember[]>([]);
  const [membersError, setMembersError] = useState<string | null>(null);
  // One spinner guard per WRITE: the member id whose capability set is being
  // saved right now (its panel's controls are disabled meanwhile).
  const [savingMemberId, setSavingMemberId] = useState<number | null>(null);
  // The member whose « Access » panel is expanded (one at a time, like the tour
  // budget panel).
  const [openAccessId, setOpenAccessId] = useState<number | null>(null);

  useEffect(() => {
    if (!canInvite || !activeProfileId) return;
    let cancelled = false;
    apiGetBandMembers(activeProfileId)
      .then((m) => { if (!cancelled) setMembers(m); })
      .catch((err: any) => { if (!cancelled) setMembersError(err?.message || 'Failed to load members'); });
    return () => { cancelled = true; };
  }, [canInvite, activeProfileId]);

  // Persist the COMPLETE capability set of one member (the server replaces the
  // matrix: capabilities absent from the array are revoked). The role is sent
  // unchanged — the matrix never touches it.
  const saveMemberPermissions = async (m: BandMember, permissions: Permission[], denied: string[]) => {
    if (!canWrite) return; // read-only: never fire the member-matrix PUT
    if (!activeProfileId || savingMemberId !== null) return;
    setSavingMemberId(m.id);
    setMembersError(null);
    try {
      await apiSetBandMember(activeProfileId, m.id, m.role, undefined, undefined, permissions, denied);
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, permissions, denied } : x)));
    } catch (err: any) {
      setMembersError(err?.message || 'Failed to update access');
    } finally {
      setSavingMemberId(null);
    }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return; // read-only: never fire the invitation POST
    if (!activeProfileId || inviteSending) return;
    setInviteSending(true);
    setInviteError(null);
    setInviteLink(null);
    try {
      // Tenant isolation: always POST against the ACTIVE band of this page —
      // never a band_id supplied by the form.
      const res = await apiCreateInvitation({
        email: inviteEmail,
        band_id: activeProfileId,
        band_role: inviteRole,
      });
      // The raw token is returned once — the server only keeps a hash.
      setInviteLink(`${window.location.origin}${res.invite_path}`);
      setInviteEmail('');
      setInviteRole('viewer');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : t('bandSettings.inviteError'));
    } finally {
      setInviteSending(false);
    }
  };

  // Promo kit public link (editor+ can see/generate it; a viewer's fetch 403s
  // and the link is simply hidden).
  useEffect(() => {
    let cancelled = false;
    if (!activeProfileId) return;
    apiGetPromoToken(activeProfileId)
      .then((r) => {
        if (cancelled) return;
        setPromoToken(r.token);
        // The password comes back DECRYPTED for a caller holding the write
        // right ('' otherwise) — that is the "read it back if forgotten" ask.
        setPromoPassword(r.password || '');
        setPromoSavedPassword(r.password || '');
        setPromoHasPassword(!!r.hasPassword);
      })
      .catch(() => { if (!cancelled) setPromoToken(null); }); // viewer/403 → hide the link
    return () => { cancelled = true; };
  }, [activeProfileId]);

  const promoKitUrl = promoToken ? `${window.location.origin}/share/promo/${promoToken}` : null;

  const copyPromoLink = async () => {
    if (!promoKitUrl) return;
    try {
      await navigator.clipboard.writeText(promoKitUrl);
      setPromoLinkCopied(true);
      setTimeout(() => setPromoLinkCopied(false), 1500);
    } catch {
      // clipboard unavailable — the URL stays visible in the field to copy manually
    }
  };

  const regeneratePromoToken = async () => {
    if (!canWrite) return; // before the confirm dialog AND the API call
    if (!activeProfileId) return;
    const ok = await showConfirm(t('bandSettings.promoRegenerateConfirm'), {
      title: t('bandSettings.promoRegenerateTitle'),
      confirmLabel: t('bandSettings.promoRegenerate'),
      danger: true,
    });
    if (!ok) return;
    try {
      const r = await apiRegeneratePromoToken(activeProfileId);
      setPromoToken(r.token);
    } catch {
      // regenerating failed — keep the current token/link
    }
  };

  // Save the password on the public Promo kit link (set or change) — and NEVER
  // rotate the link: the server leaves `promo_kit_token` untouched, so the URL
  // already handed to promoters keeps working (user decision 2026-10-16).
  const savePromoPassword = async (next: string) => {
    // Re-checked here (not only on the button): the keyboard path (Enter on the
    // field) must not fire a PUT that the account cannot make.
    if (!canWrite || !activeProfileId || promoPasswordSaving) return;
    setPromoPasswordSaving(true);
    setPromoPasswordMsg(null);
    setPromoPasswordError(null);
    try {
      const r = await apiSetPromoPassword(activeProfileId, next);
      setPromoPassword(r.password || '');
      setPromoSavedPassword(r.password || '');
      setPromoHasPassword(!!r.hasPassword);
      if (r.token) setPromoToken(r.token); // unchanged by design — proves the link survived
      setPromoPasswordMsg(r.hasPassword ? t('bandSettings.promoPasswordSaved') : t('bandSettings.promoPasswordCleared'));
    } catch (e) {
      setPromoPasswordError(e instanceof Error ? e.message : t('bandSettings.promoPasswordFailed'));
    } finally {
      setPromoPasswordSaving(false);
    }
  };

  // Re-sync the Home base fields when the band changes (profiles load async, so
  // the state initializer may have run before this band's row was available).
  useEffect(() => {
    setAddress(profile?.address || '');
    setPostalCode(profile?.postal_code || '');
    setCity(profile?.city || '');
    setLocateMsg(null);
    setLocateError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  // ── Tabs (structure-only refactor, 2026-10-19 — spec docs/SPEC-BAND-SETTINGS-TABS.md) ──
  // The long settings page is split into 4 tabs. ZERO logic change: the blocks
  // below move verbatim into per-tab panels; every hook stays at the top of the
  // component (no conditional hooks). The active tab lives in the URL hash
  // (#identity / #files / #members / #advanced) so help can deep-link a tab;
  // default = Identity.
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    const h = window.location.hash.replace('#', '');
    return VALID_SETTINGS_TABS.has(h as SettingsTab) ? (h as SettingsTab) : 'identity';
  });
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace('#', '');
      if (VALID_SETTINGS_TABS.has(h as SettingsTab)) setActiveTab(h as SettingsTab);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  // Tab bar reference: switching a settings tab also scrolls the (sticky) tab
  // bar back to the top of the window, so the selected tab is always visible
  // after scrolling deep inside a long tab's content. (2026-10-19)
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const selectTab = (tab: SettingsTab) => {
    setActiveTab(tab);
    // replaceState (not pushState): switching tabs is navigation inside one
    // page, it must not pollute the browser history.
    window.history.replaceState(null, '', `#${tab}`);
    // The tab bar is sticky (top:0), so bring it back to the top of the window
    // when switching — same pattern as ShowDetailPage: an INSTANT jump (not
    // smooth) so short-content tabs land at the same place instead of stopping
    // mid-animation; window.scrollTo with an absolute target is more reliable
    // than scrollIntoView. (2026-10-19)
    requestAnimationFrame(() => {
      if (tabBarRef.current) {
        const top = tabBarRef.current.getBoundingClientRect().top + window.scrollY - 12;
        window.scrollTo({ top, behavior: 'auto' });
      }
    });
  };

  // « Locate address » — persist the typed address FIRST (the server geocodes
  // what is stored on the band row), then ask the server to resolve + store the
  // coordinates. No automatic write: the user clicks.
  const locateAddress = async () => {
    if (!canWrite || !activeProfileId || locating) return;
    setLocating(true);
    setLocateError(null);
    setLocateMsg(null);
    try {
      await apiUpdateBand(activeProfileId, { address, postal_code: postalCode, city });
      const r = await apiGeocodeBandAddress(activeProfileId);
      updateProfile(activeProfileId, { lat: r.lat, lng: r.lng });
      setLocateMsg(t('bandSettings.homeBaseLocated', {
        coords: `${r.lat.toFixed(4)}, ${r.lng.toFixed(4)}`,
        label: r.label,
      }));
    } catch (e) {
      setLocateError(e instanceof Error ? e.message : t('bandSettings.homeBaseLocateFailed'));
    } finally {
      setLocating(false);
    }
  };

  if (!profile || !activeProfileId) {
    return (
      <div className="page">
        <p>{t('bandSettings.noProfile')}</p>
      </div>
    );
  }

  const handleSaveName = () => {
    if (!canWrite) return; // read-only: never fire PUT /api/bands/:id
    if (name.trim()) {
      if (!isNameUnique(name.trim(), profiles as any, activeProfileId)) {
        alert(t('bandSettings.nameExists'));
        return;
      }
      updateProfile(activeProfileId, { name: name.trim() });
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canWrite) return; // read-only: never fire the band PUT
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      updateProfile(activeProfileId, { image: base64 });
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = () => {
    // Entry point 4/4 of the delete path (with the trigger button, the
    // confirmation panel's render and its confirm button): DELETE /api/bands/:id
    // needs OWNER on that band (server/routes/bands.js:143) — an editor must
    // never reach this line.
    if (!canDeleteBand) return; // not owner/superadmin (or frozen): never fire DELETE /api/bands/:id
    if (deleteConfirm === profile.name) {
      deleteProfile(activeProfileId);
      navigate('/');
    }
  };

  return (
    <div ref={denseRef} className="page" data-density="socle">
      <PageHeader title={withPageIcon('bandSettings.title', t('bandSettings.title'))} />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {/* Tab bar (pattern borrowed from ShowDetailPage) — flexWrap keeps it
          readable at 390px; STICKY so the tabs stay aligned at top even for
          short-content tabs, where the page is too short to scroll the bar to
          the top. (2026-10-19) */}
      <div ref={tabBarRef} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', scrollMarginTop: 12, position: 'sticky', top: 0, zIndex: 20, padding: '8px 0', background: 'var(--bg)' }}>
        {(['identity', 'files', 'members', 'advanced'] as SettingsTab[]).map((tab) => (
          <button
            key={tab}
            className="btn"
            style={{
              flex: '0 0 auto',
              background: activeTab === tab ? 'var(--accent)' : 'var(--card)',
              color: activeTab === tab ? 'var(--on-accent)' : 'var(--text)',
              border: 'none',
            }}
            onClick={() => selectTab(tab)}
          >
            {tab === 'identity'
              ? t('bandSettings.tabIdentity')
              : tab === 'files'
                ? t('bandSettings.tabFiles')
                : tab === 'members'
                  ? t('bandSettings.tabMembers')
                  : t('bandSettings.tabAdvanced')}
          </button>
        ))}
      </div>

      <div className="card">
        {activeTab === 'identity' && (<>
        {/* Row 1: Band Name | Band Profile Image */}
        <div className="settings-row">
          <div className="form-section">
            <h2>{t('bandSettings.bandName')}</h2>
            <div className="form-group">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('bandSettings.namePh')}
                className="form-input"
                disabled={!canWrite}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={handleSaveName}
              disabled={!canWrite || !name.trim() || name === profile.name}
            >
              {t('bandSettings.saveName')}
            </button>
          </div>

          <div className="form-section">
            <h2>{t('bandSettings.bandImage')}</h2>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
              <div style={{ minWidth: '96px' }}>
                {profile.image ? (
                  <img
                    src={profile.image}
                    alt={profile.name}
                    style={{
                      width: '96px',
                      height: '96px',
                      borderRadius: '8px',
                      objectFit: 'contain',
                      border: '2px solid var(--border)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '96px',
                      height: '96px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'var(--surface)',
                      border: '2px dashed var(--border)',
                      fontSize: '40px',
                    }}
                  >
                    📸
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canWrite}
                >
                  {t('bandSettings.uploadImage')}
                </button>
                <p style={{ fontSize: '12px', color: 'var(--text-dim)', marginTop: '8px' }}>
                  {t('bandSettings.imageRecommend')}
                </p>
                {profile.image && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => { if (!canWrite) return; updateProfile(activeProfileId, { image: '' }); }}
                    disabled={!canWrite}
                    style={{ marginTop: '8px' }}
                  >
                    {t('bandSettings.removeImage')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Home Country | Band Color */}
        <div className="settings-row">
          <div className="form-section">
            <h2>{t('bandSettings.homeCountry')}</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-dim)', marginBottom: 12 }}>
              {t('bandSettings.countryHint')}
            </p>
            <div className="form-group">
              {/* CountryInput now carries an optional `disabled` prop, wired to
                  the SAME account-level gate as every neighbouring control: a
                  frozen page must LOOK frozen (the write was already short-
                  circuited on the handler, but an active-looking field is a
                  silent no-op). */}
              <CountryInput
                value={country}
                onChange={(v) => { if (!canWrite) return; setCountry(v); updateProfile(activeProfileId, { country: v }); }}
                placeholder={t('bandSettings.countryPh')}
                disabled={!canWrite}
              />
            </div>
          </div>

          <div className="form-section">
            <h2>{t('bandSettings.bandColor')}</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-dim)', marginBottom: 12 }}>
              {t('bandSettings.colorHint')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                value={color}
                onChange={(e) => { if (!canWrite) return; setColor(e.target.value); updateProfile(activeProfileId, { color: e.target.value }); }}
                disabled={!canWrite}
                style={{
                  width: 56, height: 40, padding: 4, border: '1px solid var(--border)',
                  borderRadius: 8, background: 'var(--surface)', cursor: 'pointer',
                }}
              />
              <input
                type="text"
                value={color}
                onChange={(e) => { if (!canWrite) return; setColor(e.target.value); updateProfile(activeProfileId, { color: e.target.value }); }}
                disabled={!canWrite}
                style={{ width: 110, fontFamily: 'monospace', textTransform: 'uppercase' }}
                maxLength={7}
              />
              <span style={{
                width: 22, height: 22, borderRadius: 6,
                background: color, border: '1px solid var(--border)',
              }} />
            </div>
          </div>
        </div>

        {/* Home base (2026-10-11): the band's address is the van's departure
            point for the automatic « band -> venue » distance leg. The user
            clicks « Locate address » — nothing is geocoded automatically. */}
        <div className="form-section" data-home-base style={{ marginTop: 32, paddingTop: 24 }}>
          <h2>{t('bandSettings.homeBase')}</h2>
          <p className="text-dim" style={{ marginTop: 0 }}>{t('bandSettings.homeBaseHint')}</p>
          <div className="settings-row">
            <div className="form-group">
              <label>{t('bandSettings.homeBaseAddress')}</label>
              <input
                type="text"
                className="form-input"
                value={address}
                placeholder={t('bandSettings.homeBaseAddressPh')}
                onChange={(e) => { setAddress(e.target.value); if (!canWrite) return; updateProfile(activeProfileId, { address: e.target.value }); }}
                disabled={!canWrite}
              />
            </div>
            <div className="form-group">
              <label>{t('bandSettings.homeBaseCity')}</label>
              <input
                type="text"
                className="form-input"
                value={city}
                placeholder={t('bandSettings.homeBaseCityPh')}
                onChange={(e) => { setCity(e.target.value); if (!canWrite) return; updateProfile(activeProfileId, { city: e.target.value }); }}
                disabled={!canWrite}
              />
            </div>
          </div>
          {/* Departure points (2026-10-17 / 2026-11-02 LOT U2c): TWO fields
              visible at all times, side by side on the page grid, each backed by
              its OWN catalogue — IATA airport vs UIC station. The old air/rail
              toggle is gone: its click WIPED the stored value, and a single
              field could only ever hold one of the two. */}
          <div className="settings-row" style={{ marginTop: 12 }} data-departure-points>
            <div className="form-group">
              <label>{t('bandSettings.departureAirport')}</label>
              <AirportInput
                mode="air"
                value={defaultAirport}
                placeholder={t('bandSettings.departureAirportPh')}
                onChange={(v) => { setDefaultAirport(v); if (!canWrite) return; updateProfile(activeProfileId, { default_airport: v }); }}
                disabled={!canWrite}
              />
              <small className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>{t('bandSettings.departureAirportHint')}</small>
            </div>
            <div className="form-group">
              <label>{t('bandSettings.departureStation')}</label>
              <AirportInput
                mode="rail"
                value={defaultStation}
                placeholder={t('bandSettings.departureStationPh')}
                onChange={(v) => { setDefaultStation(v); if (!canWrite) return; updateProfile(activeProfileId, { default_station: v }); }}
                disabled={!canWrite}
              />
              <small className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>{t('bandSettings.departureStationHint')}</small>
            </div>
          </div>
          <div className="settings-row" style={{ marginTop: 12 }}>
            <div className="form-group" style={{ maxWidth: 220 }}>
              <label>{t('bandSettings.homeBasePostalCode')}</label>
              <input
                type="text"
                className="form-input"
                value={postalCode}
                placeholder={t('bandSettings.homeBasePostalCodePh')}
                onChange={(e) => { setPostalCode(e.target.value); if (!canWrite) return; updateProfile(activeProfileId, { postal_code: e.target.value }); }}
                disabled={!canWrite}
              />
            </div>
          </div>
          <p className="text-dim" style={{ marginTop: 8, fontSize: 'var(--font-sm)' }}>
            {t('bandSettings.homeBaseCountryHint', { country: country || t('bandSettings.homeBaseCountryNone') })}
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
            <button
              className="btn btn-primary"
              data-locate-btn
              onClick={() => void locateAddress()}
              disabled={!canWrite || locating || !address.trim()}
            >
              {locating ? t('bandSettings.homeBaseLocating') : t('bandSettings.homeBaseLocate')}
            </button>
            {profile.lat != null && profile.lng != null && (
              <span className="text-dim" data-home-base-coords style={{ fontSize: 'var(--font-sm)' }}>
                {t('bandSettings.homeBaseSaved', { coords: `${Number(profile.lat).toFixed(4)}, ${Number(profile.lng).toFixed(4)}` })}
              </span>
            )}
          </div>
          {locateMsg && (
            <p style={{ color: 'var(--success)', marginTop: 10, fontSize: 'var(--font-sm)' }} data-home-base-msg>{locateMsg}</p>
          )}
          {locateError && (
            <p style={{ color: 'var(--danger)', marginTop: 10, fontSize: 'var(--font-sm)' }} role="alert">{locateError}</p>
          )}
        </div>

        </>)}

        {activeTab === 'files' && (<>
        {/* Files: full width (single row) */}
        <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
          <h2>{t('bandSettings.files')}</h2>
          <p className="text-dim" style={{ marginTop: 0 }}>
            {t('bandSettings.filesHint')}
          </p>
          <BandFilesSection bandId={activeProfileId} uploadHint={t('files.bandUploadHintBand')} emptyText={t('files.noBandFilesBand')} />
        </div>

        {/* Promo kit: full width (single row) — a second per-band file repo shared
            with promoters via a public token link, like advancing. (2026-08-22) */}
        <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
          <h2>{t('bandSettings.promoKit')}</h2>
          <p className="text-dim" style={{ marginTop: 0 }}>
            {t('bandSettings.promoKitHint')}
          </p>

          {promoKitUrl && (
            <div style={{ marginBottom: 16 }}>
              <p className="text-dim" style={{ marginBottom: 6 }}>{t('bandSettings.promoKitLink')}</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  readOnly
                  value={promoKitUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="form-input"
                  style={{ flex: 1, minWidth: 280, fontFamily: 'monospace', fontSize: 'var(--font-sm)' }}
                />
                <button className="btn btn-primary" onClick={() => void copyPromoLink()}>
                  {promoLinkCopied ? t('bandSettings.promoCopied') : t('bandSettings.promoCopy')}
                </button>
                <button className="btn btn-secondary" onClick={() => void regeneratePromoToken()} disabled={!canWrite}>
                  {t('bandSettings.promoRegenerate')}
                </button>
              </div>
              <p className="text-dim" style={{ marginTop: 6, fontSize: 'var(--font-xs)' }}>
                {t('bandSettings.promoKitLinkHint')}
              </p>
            </div>
          )}

          {/* Password on the public link (2026-10-16). Stored with REVERSIBLE
              encryption, so the editor READS IT BACK right here (the server
              only decrypts it for a caller holding the write right — a global
              read-only account sees this field frozen and empty, never the
              clear text). Setting / changing / clearing it NEVER rotates the
              link above: promoters who already have it keep using it. */}
          <div style={{ marginBottom: 20 }} data-promo-password>
            <p className="text-dim" style={{ marginBottom: 6 }}>{t('bandSettings.promoPasswordLabel')}</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <PasswordInput
                className="form-input"
                value={promoPassword}
                placeholder={t('bandSettings.promoPasswordPh')}
                autoComplete="new-password"
                disabled={!canWrite || promoPasswordSaving}
                onChange={(e) => {
                  setPromoPassword(e.target.value);
                  setPromoPasswordMsg(null);
                  setPromoPasswordError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  void savePromoPassword(e.currentTarget.value);
                }}
                containerStyle={{ flex: 1, minWidth: 280 }}
              />
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => void savePromoPassword(promoPassword)}
                disabled={!canWrite || promoPasswordSaving || !promoPassword.trim() || promoPassword.trim() === promoSavedPassword}
              >
                {promoPasswordSaving ? t('bandSettings.promoPasswordSaving') : t('bandSettings.promoPasswordSave')}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => void savePromoPassword('')}
                disabled={!canWrite || promoPasswordSaving || !promoHasPassword}
              >
                {t('bandSettings.promoPasswordRemove')}
              </button>
            </div>
            <p className="text-dim" style={{ marginTop: 6, fontSize: 'var(--font-xs)' }}>
              {t('bandSettings.promoPasswordHint')}
            </p>
            {promoPasswordMsg && (
              <p style={{ color: 'var(--success)', margin: '6px 0 0', fontSize: 'var(--font-sm)' }} data-promo-password-msg>
                {promoPasswordMsg}
              </p>
            )}
            {promoPasswordError && (
              <p style={{ color: 'var(--danger)', margin: '6px 0 0', fontSize: 'var(--font-sm)' }} role="alert">
                {promoPasswordError}
              </p>
            )}
          </div>

          {/* Public promo PAGE content (option C, 2026-10-19): content source
              (band default / tour) + editor of the band-default blob + preview
              of the real public page. Editor+ writes, server re-validates. */}
          <PromoContentSettings bandId={activeProfileId} token={promoToken} canWrite={canWrite} />

          <BandFilesSection bandId={activeProfileId} category="promo_kit" description={t('bandSettings.promoKitDesc')} emptyText={t('files.noBandFilesBand')} />
        </div>

        </>)}

        {activeTab === 'members' && (<>
        {/* Invite members: full width — owner/superadmin only. (2026-08-23)
            Editors/viewers don't see it; the backend enforces canManageBand. */}
        {canInvite && (
          <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
            <h2>{t('bandSettings.inviteMembers')}</h2>
            <p className="text-dim" style={{ marginTop: 0 }}>
              {t('bandSettings.inviteHint')}
            </p>

            <form onSubmit={sendInvite} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label>{t('bandSettings.inviteEmail')}</label>
                <input
                  type="email"
                  className="form-input"
                  value={inviteEmail}
                  required
                  placeholder="name@example.com"
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={!canWrite}
                />
              </div>
              <div style={{ width: 180 }}>
                <label>{t('bandSettings.inviteRole')}</label>
                <select
                  className="form-input"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as BandRole)}
                  disabled={!canWrite}
                >
                  <option value="owner">{t('bandSettings.inviteRoleOwner')}</option>
                  <option value="editor">{t('bandSettings.inviteRoleEditor')}</option>
                  <option value="tech">{t('bandSettings.inviteRoleTech')}</option>
                  <option value="viewer">{t('bandSettings.inviteRoleViewer')}</option>
                </select>
              </div>
              <button className="btn btn-primary" type="submit" disabled={inviteSending || !canWrite}>
                {inviteSending ? t('bandSettings.inviteSending') : t('bandSettings.inviteSend')}
              </button>
            </form>

            {inviteError && (
              <p style={{ color: 'var(--danger)', marginTop: 12 }}>{inviteError}</p>
            )}

            {inviteLink && (
              <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--success)', borderRadius: 8 }}>
                <strong style={{ color: 'var(--success)' }}>{t('bandSettings.inviteLinkTitle')}</strong>
                <p className="text-dim" style={{ fontSize: 'var(--font-xs)', margin: '4px 0 8px' }}>
                  {t('bandSettings.inviteLinkHint')}
                </p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    readOnly
                    value={inviteLink}
                    onFocus={(e) => e.currentTarget.select()}
                    className="form-input"
                    style={{ flex: 1, minWidth: 280, fontFamily: 'monospace', fontSize: 'var(--font-sm)' }}
                  />
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(inviteLink).then(() => {
                        setInviteCopied(true);
                        setTimeout(() => setInviteCopied(false), 1500);
                      }).catch(() => {});
                    }}
                  >
                    {inviteCopied ? t('bandSettings.inviteCopied') : t('bandSettings.inviteCopy')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Members & their capability matrix: owner/superadmin only. The old
            ad-hoc checkboxes (finance / layout) are REPLACED by one « Access »
            screen per member (chantier C, 2026-09-25). */}
        {canInvite && (
          <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
            <h2>{t('bandSettings.membersTitle')}</h2>
            <p className="text-dim" style={{ marginTop: 0 }}>
              {t('access.hint')}
            </p>
            {membersError && (
              <p style={{ color: 'var(--danger)' }}>{membersError}</p>
            )}
            {members.length === 0 && !membersError ? (
              <p className="text-dim">{t('bandSettings.membersEmpty')}</p>
            ) : (
              <div className="table-wrapper" style={{ maxWidth: 1000 }}>
                <table className="tbl-dense">
                  <thead>
                    <tr>
                      <th>{t('access.columnMember')}</th>
                      <th>{t('access.columnRole')}</th>
                      <th>{t('access.columnAccess')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => {
                      const isOwner = m.role === 'owner';
                      // DERIVED label (nothing is stored): an editor holding at
                      // An editor holding at least one `layout.*` capability
                      // shows the derived « Editor + layouts » label.
                      const roleKey = derivedRoleLabelKey(m) || ROLE_LABEL[m.role];
                      const granted = memberPermissions(m);
                      const open = openAccessId === m.id;
                      return (
                        <Fragment key={m.id}>
                          <tr>
                            <td style={{ fontWeight: 500 }}>
                              {m.display_name || m.username}
                            </td>
                            <td className="text-dim">{t(roleKey)}</td>
                            <td className="text-dim" style={{ fontSize: 'var(--font-sm)' }}>
                              {isOwner
                                ? t('access.always')
                                : granted.length > 0
                                  ? t('access.count', { n: granted.length })
                                  : t('access.none')}
                            </td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button
                                type="button"
                                className="btn btn-secondary btn-small"
                                aria-expanded={open}
                                aria-controls={`access-panel-${m.id}`}
                                onClick={() => setOpenAccessId(open ? null : m.id)}
                              >
                                {open ? t('access.close') : t('access.open')}
                              </button>
                            </td>
                          </tr>
                          {open && (
                            <tr id={`access-panel-${m.id}`} className="access-panel-row">
                              <td colSpan={4} style={{ background: 'var(--surface)' }}>
                                <AccessPanel
                                  member={m}
                                  saving={savingMemberId === m.id}
                                  onSave={(permissions, denied) => saveMemberPermissions(m, permissions, denied)}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        </>)}

        {activeTab === 'advanced' && (<>
        {/* Import TM/MT: full width (single row) — above Backup & restore. (2026-08-26) */}
        <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
          <h2>{t('importPage.title')}</h2>
          <BandImportSection bandId={activeProfileId} bandName={profile.name} />
        </div>

        {/* Backup & restore: full width (single row) */}
        <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
          <h2>{t('bandBackup.title')}</h2>
          <p className="text-dim" style={{ marginTop: 0 }}>
            {t('bandBackup.hint')}
          </p>
          <BandBackupSection bandId={activeProfileId} bandName={profile.name} />
        </div>

        <div className="form-section" style={{ marginTop: 32, paddingTop: 24 }}>
          <h2 style={{ color: 'var(--danger)' }}>{t('bandSettings.dangerZone')}</h2>
          
          {/* Danger zone — entry point 1/4 of the delete path: DELETE
              /api/bands/:id needs OWNER (server/routes/bands.js:143), so an
              editor must not even see the trigger. */}
          {/* Entry points 2/4 (this render) and 3/4 (the confirm button below):
              the gate is RE-CHECKED on both, so no path (a stale `showDelete`, a
              future caller) can show the confirmation of an action the server
              would 403 — `DELETE /api/bands/:id` is owner-only. */}
          {!showDelete || !canDeleteBand ? (
            <button
              className="btn btn-danger"
              onClick={() => setShowDelete(true)}
              disabled={!canDeleteBand}
            >
              {t('bandSettings.deleteBand')}
            </button>
          ) : (
            <div style={{ padding: '16px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '4px' }}>
              <p style={{ marginBottom: '12px', color: 'var(--danger)', fontWeight: 'bold' }}>
                {t('bandSettings.deleteConfirmHint', { name: profile.name })}
              </p>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={t('bandSettings.typeToConfirm', { name: profile.name })}
                className="form-input"
                style={{ marginBottom: '12px' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleteConfirm !== profile.name || !canDeleteBand}
                >
                  {t('bandSettings.confirmDelete')}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDelete(false);
                    setDeleteConfirm('');
                  }}
                >
                  {t('bandSettings.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}
